import { getAuth } from "firebase/auth";
import { USERS_API_BASE_URL } from "../config/api";

const BASE_URL = USERS_API_BASE_URL;

// --- INTERFACES ---

export interface ProductEntitlement {
  enabled?: boolean;
  limit?: number;
  activatedAt?: string;
}

/**
 * Response of `GET /users/:uid/products`.
 *
 * The keys are product identifiers written by the backend invite job from
 * HubSpot CRM line items (Trello #410). Known keys today:
 *   - "realtime" → Real Time product
 *   - "odm"      → Origin – Destination product
 * A key is only present if the user has purchased that product.
 */
export interface ProductsResponse {
  products: Record<string, ProductEntitlement>;
}

// --- AUTH HELPER (mirrors machineAccountsApi) ---

const getAuthHeaders = async (): Promise<HeadersInit> => {
  const auth = getAuth();
  const user = auth.currentUser;

  const headers: HeadersInit = { "Content-Type": "application/json" };

  if (user) {
    try {
      const idToken = await user.getIdToken();
      headers["Authorization"] = `Bearer ${idToken}`;
    } catch (error) {
      console.error("Failed to get Firebase ID token:", error);
      throw new Error("Authentication required - please log in");
    }
  } else {
    throw new Error("User not authenticated - please log in");
  }

  return headers;
};

// --- API SERVICE ---

export const productsApi = {
  /**
   * Fetch the products the current user has purchased.
   * Returns `{ products: {} }` for users with no entitlements.
   */
  async getProducts(): Promise<ProductsResponse> {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated - please log in");

    const url = `${BASE_URL}/users/${user.uid}/products`;
    const response = await fetch(url, {
      method: "GET",
      headers: await getAuthHeaders(),
      mode: "cors",
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `API Error ${response.status}: ${errorText || response.statusText}`
      );
    }

    return response.json();
  },
};
